import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../services/config.service';
import { UserService } from '../services/user.service';
import { formatString } from '../utils/string-utils';
import { catchError } from 'rxjs/operators';
import { firstValueFrom, lastValueFrom, of, throwError } from 'rxjs';
import { jsonToSafeString } from '../utils/string-utils';

@Injectable({
	providedIn: 'root'
})
export class ProductService {

	constructor(
		private configService: ConfigService,
		private userService: UserService,
		private http: HttpClient
	) { }

	getRevisions(id: number, pagination: any): Promise<any[]> {
		let url = formatString(this.configService.getConfig().document.documentRevisionHistoryURL, id);
		url += `?page=${pagination.page}`;
		url += `&size=${pagination.size}`;
		url += `&sort=${pagination.sort}`;

		return lastValueFrom(
			this.http.get<any[]>(url)
				.pipe(
					catchError(err => {
						console.error("Error fetching revisions:", err);
						return of([]); // Return empty array if request fails
					})
				)
		).then((response: any[]) => response.map(revision => ({
			...revision,
			createdStr: new Date(revision.createDate).toLocaleString('en-US'),
		})));
	}

	updateRevisions(revisions: any[]): Promise<any[]> {
		return lastValueFrom(
			this.http.post<any[]>(`${this.configService.getConfig().document.documentRevisionHistoryUpdateURL}`, revisions)
				.pipe(
					catchError(err => {
						console.error("Error updating revisions:", err);
						return of([]); // Return empty array if request fails
					})
				)
		).then((response: any[]) => response.map(revision => ({
			...revision,
			createdStr: new Date(revision.createDate).toLocaleString('en-US'),
		})));
	}

	updatePageMetadata(id: number, page: number, metadataHash: any): Promise<boolean> {
		const url = formatString(this.configService.getConfig().document.documentPageMetadataUpdate,
			id,
			page,
			encodeURIComponent(JSON.stringify(metadataHash))
		);
		return lastValueFrom(
			this.http.post<boolean>(url, {})
				.pipe(
					catchError(err => {
						console.error("Error updating page metadata:", err);
						return of(false); // Return empty array if request fails
					})
				)
		).then((response: boolean) => response);
	}

	revertRevision(id: number, revision: any): Promise<any> {
		const url = formatString(this.configService.getConfig().document.documentRevertURL, id, revision.versionNumber);

		return lastValueFrom(
			this.http.put(url, {})
				.pipe(
					catchError(() => {
						return of(false); // If there's an error, return false as an observable
					})
				)
		)
			.then((response: any) => {
				return response;
			})
			.catch(() => {
				return false;
			});
	}

	async getDocumentLibrary(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().document.documentLibraryURL, id);

		try {
			return await firstValueFrom(this.http.get(url, { responseType: 'text' }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getMetadataFormForDocument(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().document.documentSchemaFormURLForDoc, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getMetadataForm(libname?: string): Promise<any> {
		if (!libname) {
			return Promise.reject(new Error("Invalid library"));
		}
		const url = formatString(this.configService.getConfig().document.documentSchemaFormURL, libname);
		return lastValueFrom(
			this.http.get<any>(url)
				.pipe(
					catchError(err => {
						console.error("Error fetching metadata form:", err);
						return of([]); // Return empty array if request fails
					})
				)
		).then((response: any) => {
			response.fields = response.fields.map((field: any) => {
				const userfield = this.userService.user.properties.awfieldObjectList
					.find((x: any) => x.name === field.name);

				if (userfield) {
					// Use the user's preferences to provide field labels for the form
					field.label = userfield.displayName;

					if (!field.readOnly) {
						// If the user is designated as a read-only user of this field, force the field as readOnly
						field.readOnly = (field.readOnlyGroups || []).includes(this.userService.user.group);
					}
				}

				return field;
			});
			return response; // Ensure the full response is returned
		});
	}

	getMetadataForms(libs: any[]): Promise<any[]> {
		const libraries = libs || [];
		const promises = libraries.map(lib => this.getMetadataForm(lib)); // Use `.map()` for cleaner iteration

		return Promise.all(promises); // Returns a single Promise that resolves when all promises resolve
	}

	getMetadataSchema(lib?: string): Promise<any> {
		if (!lib) {
			return Promise.reject(new Error("Invalid lib"));
		}
		const url = formatString(this.configService.getConfig().document.documentSchemaURL, lib);

		return lastValueFrom(
			this.http.get<any>(url).pipe(
				catchError(err => {
					console.error("Error fetching metadata schema:", err);
					return throwError(() => new Error("Failed to fetch metadata schema")); // Rejects promise
				})
			)
		);
	}

	getMetadataSchemas(libs: string[]): Promise<any[]> {
		const libraries = libs || [];
		const promises = [];
		for (var i = 0; i < libraries.length; i++) {
			promises.push(this.getMetadataSchema(libraries[i]));
		};
		return Promise.all(promises);
	}

	updateMetadata(payload: any): Promise<any> {
		const formData = new FormData();
		formData.append("id", payload.id);
		formData.append("replaceFieldValues", payload.replaceFieldValues);
		formData.append("replaceAll", payload.replaceAll);

		if (payload.metadata) {
			formData.append("metadata", jsonToSafeString(JSON.stringify(payload.metadata)));
		}
		if (payload.replacements) {
			formData.append("replacements", JSON.stringify(payload.replacements));
		}

		const url = this.configService.getConfig().document.documentEditMetadataURL;

		return lastValueFrom(
			this.http.post<any>(url, formData)
				.pipe(
					catchError(err => {
						console.error("Error updating metadata:", err);
						return throwError(() => new Error("Failed to update metadata"));
					})
				)
		);
	}

	massUpdateMetadata(payload: any): Promise<boolean> { 
		let link = this.configService.getConfig().document.documentMassEditURL;
		link += `?replaceFieldValues=${encodeURIComponent(payload.replaceFieldValues)}`;
		link += `&replaceAll=${encodeURIComponent(payload.replaceAll)}`;

		// TODO FIXME: Why are metadata and replacements URL parameters and not part of the request body?
		if (payload.metadata) {
			link += `&metadata=${jsonToSafeString(JSON.stringify(payload.metadata))}`;
		}
		if (payload.replacements) {
			link += `&replacements=${encodeURIComponent(JSON.stringify(payload.replacements))}`;
		}

		return lastValueFrom(
			this.http.post<boolean>(link, payload.ids)
				.pipe(
					catchError(err => {
						console.error("Error in mass updating metadata:", err);
						return throwError(() => new Error("Failed to update metadata"));
					})
				)
		);
	}

	extractEntities(payload: any): Promise<string> {
		const url = formatString(this.configService.getConfig().document.documentEntityExtractURL, payload.ids[0]) + "?isPlaces=" + payload.isPlaces + "&isCoordinates=" + payload.isCoordinates + "&isCountries=" + payload.isCountries;
		return lastValueFrom(
			this.http.post<string>(url, [])
				.pipe(
					catchError(err => {
						console.error("Error in mass updating metadata:", err);
						return throwError(() => new Error("Failed to extract entities"));
					})
				)
		);
	}

	deleteDocuments(ids: string[] | string | number[] | number): Promise<boolean> {
		let normalizedIds: (string | number)[] = Array.isArray(ids) ? ids : [ids];
		let payload = { ids: normalizedIds };
		const url = this.configService.getConfig().document.documentMassDeleteURL;
		return lastValueFrom(
			this.http.post<boolean>(url, payload)
				.pipe(
					catchError(err => {
						console.error("Error in mass delete documents:", err);
						return throwError(() => new Error("Failed to extract entities"));
					})
				)
		);
	}

	getMetadata(id: number): Promise<any> {
		let params = this.configService.getConfig().search.metadataParms;
		params.COVERAGE = id;
		const queryString = this.getQueryString(params);
		const url = `${this.configService.getConfig().search.wcsURL}?${queryString}`;
		return lastValueFrom(
			this.http.get<any>(url)
				.pipe(
					catchError(err => {
						console.error("Error in mass delete documents:", err);
						return throwError(() => new Error("Failed to extract entities"));
					})
				)
		);
	}

	async getMetadataAsFormat(id: number, format: string): Promise<any> {
		let url = formatString(this.configService.getConfig().document.metadataViewAsURL, format, id);

		try {
			return await firstValueFrom(this.http.get(url, { responseType: 'text' }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	sendProducts(ids: number[], payload: any): Promise<boolean> {
		const queryString = this.getQueryString(payload); //new URLSearchParams(payload).toString();
		const url = `${this.configService.getConfig().document.sendProductsURL}?${queryString}`;
		return lastValueFrom(
			this.http.post<boolean>(url, ids)
				.pipe(
					catchError(err => {
						console.error("Error in emailing products:", err);
						return throwError(() => new Error("Failed to extract entities"));
					})
				)
		);
	}

	sendProductPage(payload: any): Promise<boolean> {
		const queryString = this.getQueryString(payload);
		const url = `${this.configService.getConfig().document.sendProductPageURL}?${queryString}`;
		return lastValueFrom(
			this.http.post<boolean>(url, {})
				.pipe(
					catchError(err => {
						console.error("Error in emailing product page:", err);
						return throwError(() => new Error("Failed to extract entities"));
					})
				)
		);
	}
	moveProducts(ids: number[], payload: { library: string }): Promise<boolean> {
		const url = this.configService.getConfig().document.moveProductToLibrary;
		const data = {
			newLibraryId: payload.library, // Fixed typo: "newLibaryId" → "newLibraryId"
			ids: ids
		};

		return lastValueFrom(
			this.http.post<boolean>(url, data).pipe(
				catchError(err => {
					console.error("Error moving products:", err);
					return throwError(() => new Error("Failed to move products"));
				})
			)
		);
	}

	rotateProductsImages(ids: number[], payload: any): Promise<boolean> {
		const url = `${this.configService.getConfig().document.rotateProductImageURL}?angle=${payload.angle}`;
		const data = {
			ids: ids
		};

		return lastValueFrom(
			this.http.post<boolean>(url, data).pipe(
				catchError(err => {
					console.error("Failed to rotate products:", err);
					return throwError(() => new Error("Failed to rotate products"));
				})
			)
		);
	}

	addWorkflowList(): Promise<string[]> {
		const url = this.configService.getConfig().document.addWorkflowListURL;
		return lastValueFrom(
			this.http.get<string[]>(url)
				.pipe(
					catchError(err => {
						console.error("Error to get addWorkflowList:", err);
						return throwError(() => new Error("Failed to get 'add' workflow list"));
					})
				)
		);
	}

	detectWorkflowList(): Promise<string[]> {
		const url = this.configService.getConfig().document.detectWorkflowListURL;
		return lastValueFrom(
			this.http.get<string[]>(url)
				.pipe(
					catchError(err => {
						console.error("Error to get detectWorkflowList:", err);
						return throwError(() => new Error("Failed to get 'detect' workflow list"));
					})
				)
		);
	}

	validateLink(uuid: string): Promise<boolean> {
		const url = formatString(this.configService.getConfig().document.validShareLinkURL, uuid);
		return lastValueFrom(
			this.http.get<boolean>(url)
				.pipe(
					catchError(err => {
						console.error("Error to validateLink:", err);
						return throwError(() => new Error("Failed to validate link"));
					})
				)
		);
	}

	displayShareLink(linkuuid: string): void {
		const url = formatString(this.configService.getConfig().document.displayProductShareLinkURL, linkuuid);
		this.http.get(url)
			.pipe(
				catchError(err => {
					console.error("Error to displayShareLink:", err);
					return throwError(() => new Error("Failed to display share link"));
				})
			).subscribe({
				next: response => {
					console.log("Share link data received:", response);
					// Handle the response if needed
				},
				error: err => {
					console.error("Failed to fetch share link:", err);
				}
			});
	}

	getProductShareLinks(uuid: string): Promise<{ [key: string]: string }> {
		const url = formatString(this.configService.getConfig().document.getProductShareLinks, uuid);
		return lastValueFrom(
			this.http.get<{ [key: string]: string }>(url).pipe(
				catchError(err => {
					console.error("Failed to getProductShareLinks:", err);
					return throwError(() => new Error("Failed to get links"));
				})
			)
		);
	}

	getBatchProductShareLinks(uuids: string[]): Promise<{ [key: string]: { [key: string]: string } }> {
		const url = this.configService.getConfig().document.getMassProductShareLinks;
		return lastValueFrom(
			this.http.post<{ [key: string]: { [key: string]: string } }>(url, uuids).pipe(
				catchError(err => {
					console.error("Failed to getBatchProductShareLinks:", err);
					return throwError(() => new Error("Failed to get share links"));
				})
			)
		);
	}

	ocrDocuments(ids: string[] | number[]): Promise<boolean> {
		if (!Array.isArray(ids)) { ids = [ids]; }
		const url = this.configService.getConfig().document.ocrProductsURL;
		return lastValueFrom(
			this.http.post<boolean>(url, ids).pipe(
				catchError(err => {
					console.error("Failed to ocr:", err);
					return throwError(() => new Error("Failed to OCR"));
				})
			)
		);
	}

	downloadProducts(payload: any, params: any): Promise<boolean> {
		const queryString = this.getQueryString(params);
		const url = `${this.configService.getConfig().document.downloadProductsURL}?${queryString}`;
		return lastValueFrom(
			this.http.put(url, payload).pipe(
				catchError(err => {
					console.error("Failed to downloadProducts:", err);
					return throwError(() => new Error("Failed to download"));
				})
			)
		).then(() => true);
	}

	setPrimaryProductPage(productId: string | number, pageId: number): Promise<boolean> {
		const url = `/aw-server/rest/product/action/setPrimaryPage?id=${productId}&page=${pageId}`;
		return lastValueFrom(
			this.http.get<boolean>(url).pipe(
				catchError(err => {
					console.error("Failed to setPrimaryProductPage:", err);
					return throwError(() => new Error("Failed to set primary page"));
				})
			)
		);
	}

	deleteProductPage(productId: string | number, pageId: number): Promise<boolean> {
		let isValidProductId = typeof productId === 'string' ? productId.trim().length > 0 : productId != null;
		if (!isValidProductId || pageId == null) {
			return Promise.reject(new Error("Invalid productId or pageId"));
		}
		const url = `/aw-server/rest/product/action/removePage?id=${productId}&page=${pageId}`;
		return lastValueFrom(
			this.http.delete<boolean>(url).pipe(
				catchError(err => {
					console.error("Failed to delete product page:", err);
					return throwError(() => new Error("Failed to delete product page"));
				})
			)
		);
	}

	addProductPage(productId: string, uploadFile: File): Promise<boolean> {
		if (!productId?.trim() || !uploadFile) {
			return Promise.reject(new Error("Invalid productId or uploadFile"));
		}

		const formData = new FormData();
		formData.append("productFile", uploadFile, uploadFile.name);

		const url = `/aw-server/rest/product/action/addPage?id=${encodeURIComponent(productId)}`;

		return lastValueFrom(
			this.http.post<boolean>(url, formData).pipe(
				catchError(err => {
					console.error("Failed to add product page:", err);
					return throwError(() => new Error("Failed to add product page"));
				})
			)
		);
	}

	updateSourceFiles(productId: string, uploadFiles: File[]): Promise<boolean> {
		if (!productId?.trim() || !uploadFiles?.length) {
			return Promise.reject(new Error("Invalid productId or uploadFiles"));
		}

		const formData = new FormData();
		uploadFiles.forEach(file => formData.append("files[]", file, file.name));
		const url = `/aw-server/rest/product/action/updateSourceFiles/${encodeURIComponent(productId)}`;

		return lastValueFrom(
			this.http.post<boolean>(url, formData).pipe(
				catchError(err => {
					console.error("Error updateSourceFiles:", err);
					return throwError(() => new Error("Failed to update source files"));
				})
			)
		);
	}

	reportFeedback(id: number, payload: any): Promise<boolean> {
		if (!id || !payload || !payload.productlink || !payload.username || !Array.isArray(payload.images)) {
			return Promise.reject(new Error("Invalid input parameters"));
		}
		const formData = new FormData();
		formData.append("link", payload.productlink);
		formData.append("username", payload.username);
		formData.append("comments", JSON.stringify(payload.comments || ""));

		payload.images.forEach((image: File) => formData.append("images", image));
		const url = `/aw-server/rest/product/action/feedback?id=${id}`;

		return lastValueFrom(
			this.http.post<boolean>(url, formData).pipe(
				catchError(err => {
					console.error("Error in reportFeedback:", err);
					return throwError(() => new Error("Failed to submit feedback"));
				})
			)
		);
	}

	lockProduct(id: number): Promise<any> {
		if (!id) {
			return Promise.reject(new Error("Invalid id"));
		}

		const url = formatString(this.configService.getConfig().document.documentLock, id);

		return lastValueFrom(
			this.http.post<any>(url, {}).pipe(
				catchError(err => {
					console.error("Error locking product:", err);
					return throwError(() => new Error("Failed to lock product"));
				})
			)
		).then(response => {
			let lock = (typeof response === "string") ? JSON.parse(response) : response;
			if (lock.lockType === "EDIT") {
				return lock;
			} else {
				return Promise.reject(this.userService.LOCALIZATION.lockedByOther);
			}
		});
	}

	unlockProduct(id: number): Promise<boolean> {
		if (!id) {
			return Promise.reject(new Error("Invalid id"));
		}

		const url = formatString(this.configService.getConfig().document.documentUnlock, id);

		return lastValueFrom(
			this.http.post<boolean>(url, {}).pipe(
				catchError(err => {
					console.error("Error unlocking product:", err);
					return throwError(() => new Error("Failed to unlock product"));
				})
			)
		);
	}

	isLocked(id: number): Promise<any> {
		if (!id) {
			return Promise.reject(new Error("Invalid id"));
		}
		const url = formatString(this.configService.getConfig().document.hasCheckout, id);
		return lastValueFrom(
			this.http.get<any>(url).pipe(
				catchError(err => {
					console.error("Error checking lock status:", err);
					return throwError(() => new Error("Failed to check product lock status"));
				})
			)
		);
	}

	checkoutProduct(id: number): Promise<any> {
		if (!id) {
			return Promise.reject(new Error("Invalid id"));
		}

		const url = formatString(this.configService.getConfig().document.documentCheckout, id);

		return lastValueFrom(
			this.http.post<any>(url, {}).pipe(
				catchError(err => {
					console.error("Error checkout product:", err);
					return throwError(() => new Error("Failed to checkout product"));
				})
			)
		).then(response => {
			let lock = response;
			if (lock.lockType === "CHECKOUT") {
				return lock;
			} else {
				return Promise.reject(this.userService.LOCALIZATION.lockedByOther);
			}
		});
	}

	checkinProduct(productId: string, sourceFile?: File): Promise<boolean> {
		if (!productId?.trim() || !sourceFile) {
			return Promise.reject(new Error("Invalid productId or uploadFile"));
		}
		const formData = new FormData();
		formData.append("productFile", sourceFile, sourceFile.name);

		const url = formatString(this.configService.getConfig().document.documentCheckin, productId);

		return lastValueFrom(
			this.http.post<boolean>(url, formData).pipe(
				catchError(err => {
					console.error("Error checkin product:", err);
					return throwError(() => new Error("Failed to checkin product"));
				})
			)
		);
	}

	undoCheckout(id: number): Promise<boolean> {
		if (!id) {
			return Promise.reject(new Error("Invalid id"));
		}

		const url = formatString(this.configService.getConfig().document.documentUndoCheckout, id);

		return lastValueFrom(
			this.http.post<boolean>(url, {}).pipe(
				catchError(err => {
					console.error("Error undo checkout product:", err);
					return throwError(() => new Error("Failed to undo checkout"));
				})
			)
		);
	}

	printProductMetadata(ids: number[], payload: any): Promise<string> {
		if (!ids?.length || !payload) {
			return Promise.reject(new Error("Invalid ids or payload"));
		}
		const queryString = this.getQueryString(payload);
		const url = `${this.configService.getConfig().document.printMetadataURL}?${queryString}`;
		return lastValueFrom(
			this.http.post(url, ids, { responseType: 'text' }).pipe(
				catchError(err => {
					console.error("Error printProductMetadata:", err);
					return throwError(() => new Error("Failed to print product metadata"));
				})
			)
		);
	}

	async getPrintProductId(productId: number | string): Promise<string> {
		let url = formatString(this.configService.getConfig().document.documentPrepPrintURL, productId);

		const response = await firstValueFrom(this.http.get<any>(url, {}));
		let printId = response.printID ?? null;

		if (printId === null) {
			throw new Error('Failed to obtain printID for product.');
		}

		return printId;
	}

	/**
	 * Opens a new window to print the product.
	 */
	printProduct(productId: number | string, printId: string): void {
		var printUrl = formatString(this.configService.getConfig().document.documentPrintURL, productId, printId);

		let documentContent = `
		  <!doctype html>
		  <html>
		    <head>
		      <meta charset="utf-8">
		      <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1">
		      <title>Knowvation - Print Document</title>
		      <script>
		        function closePrint () {
		          document.body.removeChild(this.__container__);
		        }
		        
		        function setPrint() {
		          this.contentWindow.__container__ = this;
		          this.contentWindow.onbeforeunload = closePrint;
		          this.contentWindow.onafterprint = closePrint;
		          this.contentWindow.focus();
		          this.contentWindow.print();
		        }

		        function printPage(sURL) {
		          var oHiddenFrame = document.createElement("iframe");
		          oHiddenFrame.onload = setPrint;
		          oHiddenFrame.style.position = "fixed";
		          oHiddenFrame.style.right = "0";
		          oHiddenFrame.style.bottom = "0";
		          oHiddenFrame.style.width = "0";
		          oHiddenFrame.style.height = "0";
		          oHiddenFrame.style.border = "0";
		          oHiddenFrame.src = sURL;
		          document.body.appendChild(oHiddenFrame);
		        }

		        window.onload = function() {
		          printPage("${printUrl}");
		        };
		      </script>
		    </head>
		    <body>
		    </body>
		  </html>
		`;

		let printWin: Window | null = window.open('', '_blank');
		if (printWin) {
			printWin.document.open();
			printWin.document.write(documentContent);
			printWin.document.close();
		} else {
			console.error('Failed to open a new window. Check your popup blocker settings.');
		}
	}

	getChipFormats(id: number): Promise<any> {
		if (!id) {
			return Promise.reject(new Error("Invalid id"));
		}
		let params = this.configService.getConfig().search.chipFormatParms;
		params.COVERAGE = id;
		const queryString = this.getQueryString(params);
		const url = `${this.configService.getConfig().search.wcsURL}?${queryString}`;
		return lastValueFrom(
			this.http.get<any>(url).pipe(
				catchError(err => {
					console.error("Error checkout product:", err);
					return throwError(() => new Error("Failed to checkout product"));
				})
			)
		).then(response => {
			// Using optional chaining for simpler and more readable checks
			const supportedFormats = response?.["wcs:CoverageDescription"]?.["wcs:CoverageOffering"]?.["wcs:supportedFormats"]?.["wcs:formats"];

			if (supportedFormats) {
				return supportedFormats;
			} else {
				return Promise.reject(new Error("No supported formats found"));
			}
		});
	}

	doChip(ids: number[], chip: any): Promise<any> {
		if (!ids?.length || !chip) {
			return Promise.reject(new Error("Invalid ids or chip parameters"));
		}
		let params = this.configService.getConfig().search.chipParms;
		params.COVERAGE = ids.join(',');
		params.FORMAT = chip.chipFormat;
		params.requestName = chip.chipRequestName;
		params.packageType = chip.chipPackageType;
		if (chip.chipEmailNotification) {
			params.emailNotification = chip.chipEmailAddress
		};
		if (chip.bbox) {
			params.BBOX = chip.bbox;
		};
		if (chip.pxCoordinates) {
			params.pxCoordinates = chip.pxCoordinates;
		};
		const queryString = this.getQueryString(params);
		const url = `${this.configService.getConfig().search.wcsURL}?${queryString}`;
		return lastValueFrom(
			this.http.get<any>(url).pipe(
				catchError(err => {
					console.error("Error checkout product:", err);
					return throwError(() => new Error("Failed to checkout product"));
				})
			)
		);
	}

	generateThumbnails(ids: string[]): Promise<boolean> {
		if (!ids?.length) {
			return Promise.reject(new Error("Invalid ids"));
		}
		const url = this.configService.getConfig().document.documentMassThumbnailCreation;

		return lastValueFrom(
			this.http.post<boolean>(url, ids).pipe(
				catchError(err => {
					console.error("Error generateThumbnails:", err);
					return throwError(() => new Error("Failed to generate thumbnails"));
				})
			)
		);
	}

	getQueryString(params: { [key: string]: string }): string {
		const queryString = Object.keys(params)
			.filter(key => params[key] != null)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
			.join('&');
		return queryString;
	}
}